import React, { useState, useRef, useEffect } from 'react';
import { 
    X, 
    Search, 
    Menu, 
    ChevronDown, 
    User, 
    LogOut, 
    MapPin, 
    Phone, 
    Mail, 
    Clock, 
    Instagram, 
    Linkedin, 
    FileText, 
    Trash2,
    ArrowRight
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { industries } from '../utils';
import { useAuth } from '../context/AuthContext';
import { getPublicImageUrl } from '../config/storage';
import './Navbar.css';
import LoginForm from './LoginForm';
import UserRegistration from './UserRegistration';
import { catalogStructure } from '../config/catalogData';
import { categoryIconMap } from './PPEIcons';
import BrandImage from './BrandImage';
import MapPopup from './MapPopup';
import QuoteRequestModal from './QuoteRequestModal';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { toast } from 'react-toastify';

export interface CartItem {
    _id: string;
    name: string;
    price: string;
    quantity: number;
    category: string;
    brand: string;
    image_url?: string;
}

interface NavbarProps {
    onCartClick?: () => void;
    cartItemsCount: number;
    cartItems?: CartItem[];
    onUpdateQuantity?: (id: string, quantity: number) => void;
    onRemoveItem?: (id: string) => void;
}

export const PARTNER_BRANDS = [
    { name: '3M', logo: 'https://www.lubeseguridad.com.ar/scroll/000200122757900010001201913m.png' },
    { name: 'LAKELAND', logo: 'https://www.lakeland.com/wp-content/uploads/elementor/thumbs/lakeland-fire-safety-white-225x57-1-qt736ti477vx1tra2mah3yomp0dijclyphy2wim6ii.webp' },
    { name: 'WYPALL', logo: 'https://d1yjjnpx0p53s8.cloudfront.net/styles/logo-thumbnail/s3/052013/wypall.png?itok=hH7vqNTt' },
    { name: 'LIBUS', logo: 'https://www.lubeseguridad.com.ar/scroll/0002000928860marca-libus.jpg' },
    { name: 'STEELPRO', logo: 'https://www.lubeseguridad.com.ar/scroll/0002000528948marca-steel.jpg' },
    { name: 'OMBU', logo: 'https://www.lubeseguridad.com.ar/scroll/00020008275380001001120242ombu.png' },
    { name: 'FUNCIONAL', logo: 'https://www.lubeseguridad.com.ar/scroll/00020002275380001001020242funcional.png' },
    { name: 'PAMPERO', logo: 'https://pampero.com.ar/wp-content/uploads/2024/07/logosimbolo.png' },
    { name: 'GRAFA70', logo: 'https://grafa70.com.ar/wp-content/uploads/2019/03/cropped-logo_1-2.png' },
    { name: 'MARTOR', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b2/Martor_Logo.svg/960px-Martor_Logo.svg.png?20140805082504' },
    { name: 'GAMISOL', logo: 'https://www.gamisol.com.ar/wp-content/uploads/2023/12/isologotipoGamisol.svg' },
    { name: 'EAGLE', logo: 'https://eaglesafety.com/wp-content/uploads/2019/09/eagle-safety-logo-small.png' },
    { name: 'BILVEX', logo: 'https://112cc5d764.cbaul-cdnwnd.com/9f0af1af3b99aa2b896bd83211c7b1c6/system_preview_detail_200000030-886bc89675-public/LOGO%20BIL%20VEX.jpg' },
    { name: 'PRENTEX', logo: 'https://www.cas-seguridad.org.ar/wp-content/uploads/2020/08/logo-prentex.png' },
    { name: 'CONOFLEX', logo: 'https://segutex.com.ar/wp-content/uploads/2016/05/logo-conoflex.png' },
    { name: 'MAPA', logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRJphoHPOqPZSOxa9WoIxRzP_XN-MJ8pvwMow&s' },
    { name: 'DP', logo: 'https://depascale.com.ar/wp-content/uploads/2025/07/De_Pascale_logo.png' },
    { name: 'UCU', logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRvK05ZX2xngnZgkhvCida_KjPOGhp2FgNczA&s' }
];

const Navbar: React.FC<NavbarProps> = ({
    onCartClick,
    cartItemsCount,
    cartItems = [],
    onUpdateQuantity,
    onRemoveItem,
}) => {
    const { isAuthenticated, logout, userProfile, user, isAdmin } = useAuth();
    
    const [showLogin, setShowLogin] = useState(false);
    const [isRegistering, setIsRegistering] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showProductsSearch, setShowProductsSearch] = useState(false);
    const [showIndustriesDropdown, setShowIndustriesDropdown] = useState(false);
    const [showBrandsDropdown, setShowBrandsDropdown] = useState(false);
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const [showQuoteDropdown, setShowQuoteDropdown] = useState(false);
    
    // Mobile states
    const [showIndustriesMenu, setShowIndustriesMenu] = useState(false);
    const [showBrandsMenu, setShowBrandsMenu] = useState(false);
    const [expandedSubgroups, setExpandedSubgroups] = useState<Set<string>>(new Set());
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [showQuoteModal, setShowQuoteModal] = useState(false);

    const navigate = useNavigate();
    const navSearchInputRef = useRef<HTMLInputElement>(null);
    const productsSearchRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const userMenuRef = useRef<HTMLDivElement>(null);
    const quoteDropdownRef = useRef<HTMLDivElement>(null);
    const searchDropdownRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    // Cerrar el modal de login cuando el usuario se autentica
    useEffect(() => {
        if (isAuthenticated) {
            setShowLogin(false);
            setIsRegistering(false);
        }
    }, [isAuthenticated]);

    // Precargar logos de marcas para despliegue instantáneo en el dropdown
    useEffect(() => {
        PARTNER_BRANDS.forEach((brand) => {
            if (brand.logo) {
                const img = new Image();
                img.src = brand.logo;
            }
        });
    }, []);

    // Limpiar término de búsqueda al cambiar de ruta
    useEffect(() => {
        setSearchTerm('');
        setShowProductsSearch(false);
        setShowIndustriesDropdown(false);
        setShowBrandsDropdown(false);
        setShowContactDropdown(false);
        setShowSearchDropdown(false);
        setShowQuoteDropdown(false);
        setIsMenuOpen(false);
    }, [location.pathname, location.search]);

    const handleLoginClick = () => {
        setIsRegistering(false);
        setShowLogin(true);
    };

    const handleCloseLogin = () => {
        setShowLogin(false);
        setIsRegistering(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (productsSearchRef.current && !productsSearchRef.current.contains(target)) {
                setShowProductsSearch(false);
            }
            if (menuRef.current && !menuRef.current.contains(target)) {
                setIsMenuOpen(false);
                setShowIndustriesMenu(false);
                setShowBrandsMenu(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(target)) {
                setShowUserMenu(false);
            }
            if (quoteDropdownRef.current && !quoteDropdownRef.current.contains(target)) {
                setShowQuoteDropdown(false);
            }
            if (searchDropdownRef.current && !searchDropdownRef.current.contains(target)) {
                setShowSearchDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMenuOpen]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            const searchValue = searchTerm.trim();
            setSearchTerm('');
            setShowSearchDropdown(false);
            setShowProductsSearch(false);
            setIsMenuOpen(false);
            navigate(`/catalog?search=${encodeURIComponent(searchValue)}`);
        }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setShowSearchDropdown(false);
            setShowProductsSearch(false);
        }
    };

    const handleIndustryClick = (industryName: string) => {
        navigate(`/catalog?industry=${encodeURIComponent(industryName)}`);
        setShowIndustriesMenu(false);
        setShowIndustriesDropdown(false);
        setIsMenuOpen(false);
    };

    const handleBrandClick = (brandName: string) => {
        navigate(`/catalog?brand=${encodeURIComponent(brandName)}`);
        setShowBrandsDropdown(false);
        setShowBrandsMenu(false);
        setIsMenuOpen(false);
    };

    const handleContactClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setShowContactDropdown(false);
        setIsMenuOpen(false);

        if (location.pathname === '/') {
            const footer = document.querySelector('footer');
            if (footer) {
                footer.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            navigate('/');
            setTimeout(() => {
                const footer = document.querySelector('footer');
                if (footer) {
                    footer.scrollIntoView({ behavior: 'smooth' });
                }
            }, 300);
        }
    };

    const handleProductSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            const searchValue = searchTerm.trim();
            setSearchTerm('');
            setShowProductsSearch(false);
            navigate(`/catalog?search=${encodeURIComponent(searchValue)}`);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            setShowUserMenu(false);
        } catch (error) {
            console.error('Error al cerrar sesión:', error);
        }
    };

    const handleSwitchToRegister = () => {
        setIsRegistering(true);
    };

    const handleSwitchToLogin = () => {
        setIsRegistering(false);
    };

    const handleCategoryClick = (categoryId: string, subcategoryId?: string) => {
        setShowProductsSearch(false);
        setIsMenuOpen(false);
        let url = `/catalog?category=${encodeURIComponent(categoryId)}`;
        if (subcategoryId) {
            url += `&subcategory=${encodeURIComponent(subcategoryId)}`;
        }
        navigate(url);
    };

    const toggleSubgroup = (subgroupId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedSubgroups(prev => {
            const next = new Set(prev);
            if (next.has(subgroupId)) {
                next.delete(subgroupId);
            } else {
                next.add(subgroupId);
            }
            return next;
        });
    };

    // Envío de cotización
    const handleQuoteRequest = async (data: { fullName: string; company: string; email: string }) => {
        const loadingToast = toast.loading('Enviando solicitud de cotización...');
        try {
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3010';
            const response = await fetch(`${API_URL}/api/send-quote`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    customerData: data,
                    items: cartItems.map(item => ({
                        name: item.name,
                        brand: item.brand,
                        category: item.category,
                        quantity: item.quantity,
                        _id: item._id
                    })),
                    userProfile: isAuthenticated ? {
                        phone: userProfile?.phone || 'No especificado',
                        address: userProfile?.address || 'No especificado',
                        city: userProfile?.city || 'No especificado',
                        industry: userProfile?.industry || 'No especificado'
                    } : null
                })
            });

            const result = await response.json();

            if (response.ok && result.success) {
                toast.dismiss(loadingToast);
                toast.success('¡Cotización enviada exitosamente! Te contactaremos a la brevedad.');
                cartItems.forEach(item => onRemoveItem?.(item._id));
                setShowQuoteModal(false);
                setShowQuoteDropdown(false);
            } else {
                throw new Error(result.error || 'Error al enviar la cotización');
            }
        } catch (error) {
            console.error('Error al enviar cotización:', error);
            toast.dismiss(loadingToast);
            toast.error('Error al enviar la cotización. Por favor, intente nuevamente.');
        }
    };

    const totalQuotePrice = cartItems.reduce((acc, i) => acc + (Number(i.price) || 0) * i.quantity, 0);

    const renderAuthButtons = () => {
        if (isAuthenticated) {
            const displayName = userProfile?.name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'Usuario';
            return (
                <div className="relative" ref={userMenuRef}>
                    <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="flex items-center space-x-1.5 text-amber-300 hover:text-amber-400 text-sm font-medium py-1 px-2 rounded-lg hover:bg-amber-400/10 transition-colors"
                    >
                        <User className="h-4 w-4" />
                        <span className="truncate max-w-[110px]">{displayName}</span>
                        <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    
                    {showUserMenu && (
                        <div className="absolute right-0 mt-2 w-52 rounded-xl shadow-2xl bg-black/95 backdrop-blur-md border border-amber-300/30 z-50 overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-amber-300/10 bg-amber-500/5">
                                <p className="text-xs text-gray-400">Sesión iniciada como</p>
                                <p className="text-sm font-semibold text-amber-300 truncate">{displayName}</p>
                                <span className="inline-block mt-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-amber-400/20 text-amber-300 border border-amber-400/30">
                                    {isAdmin ? 'Administrador' : 'Cliente'}
                                </span>
                            </div>
                            <div className="py-1">
                                <Link
                                    to="/perfil"
                                    className="block px-4 py-2.5 text-sm text-amber-300 hover:bg-amber-300/10 transition-colors"
                                    onClick={() => setShowUserMenu(false)}
                                >
                                    Mi Perfil & Datos
                                </Link>
                                <Link
                                    to="/pedidos"
                                    className="block px-4 py-2.5 text-sm text-amber-300 hover:bg-amber-300/10 transition-colors"
                                    onClick={() => setShowUserMenu(false)}
                                >
                                    Mis Pedidos & Estado
                                </Link>
                                {isAdmin && (
                                    <Link
                                        to="/admin"
                                        className="block px-4 py-2.5 text-sm text-amber-300 hover:bg-amber-300/10 border-t border-amber-300/10 font-semibold"
                                        onClick={() => setShowUserMenu(false)}
                                    >
                                        Panel de Control Admin
                                    </Link>
                                )}
                                <button
                                    onClick={handleLogout}
                                    className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 border-t border-amber-300/10 transition-colors"
                                >
                                    <div className="flex items-center">
                                        <LogOut className="h-4 w-4 mr-2" />
                                        Cerrar Sesión
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <button
                onClick={handleLoginClick}
                className="action-button px-3.5 py-1.5 text-black font-semibold text-sm rounded-lg hover:shadow-lg transition-all"
            >
                Ingreso
            </button>
        );
    };

    const renderMobileAuthButtons = () => {
        if (isAuthenticated) {
            const displayName = userProfile?.name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'Usuario';
            return (
                <>
                    <div className="text-center pb-2 border-b border-amber-300/20 mb-2">
                        <p className="text-sm text-gray-400">Hola, {displayName}</p>
                        <span className="inline-block mt-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-amber-400/20 text-amber-300">
                            {isAdmin ? 'Administrador' : 'Cliente'}
                        </span>
                    </div>
                    <Link 
                        to="/perfil" 
                        className="block text-xl text-amber-300 hover:text-amber-400 py-2 w-full text-center"
                        onClick={() => setIsMenuOpen(false)}
                    >
                        Mi Perfil & Datos
                    </Link>
                    <Link 
                        to="/pedidos" 
                        className="block text-xl text-amber-300 hover:text-amber-400 py-2 w-full text-center"
                        onClick={() => setIsMenuOpen(false)}
                    >
                        Mis Pedidos & Estado
                    </Link>
                    {isAdmin && (
                        <Link 
                            to="/admin" 
                            className="block text-xl text-amber-300 hover:text-amber-400 py-2 w-full text-center font-bold"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            Panel Admin
                        </Link>
                    )}
                    <button
                        onClick={handleLogout}
                        className="w-full text-xl text-red-400 hover:text-red-300 py-2 flex items-center justify-center space-x-2"
                    >
                        <LogOut className="h-5 w-5" />
                        <span>Cerrar Sesión</span>
                    </button>
                </>
            );
        }

        return (
            <button
                onClick={() => {
                    handleLoginClick();
                    setIsMenuOpen(false);
                }}
                className="w-full text-xl text-amber-300 hover:text-amber-400 py-2 text-center"
            >
                Iniciar Sesión
            </button>
        );
    };

    return (
        <>
            <header className="navbar-container">
                <nav>
                    <div className="container mx-auto px-4">
                        <div className="flex justify-between items-center h-12 md:h-14">
                            {/* Logo Flotante */}
                            <div className="flex-shrink-0 relative flex items-center h-full">
                                {/* Tres estrellas centradas dentro del navbar */}
                                <span className="absolute -left-12 top-1/2 -translate-y-1/2 text-gray-500 stars-container pointer-events-none" aria-label="Tres estrellas campeonato mundial">
                                    <span className="stars-size">★</span>
                                    <span className="relative -top-1 stars-size">★</span>
                                    <span className="stars-size">★</span>
                                </span>

                                {/* Placeholder invisible para mantener el espaciado en la barra */}
                                <div className="w-14 md:w-20 lg:w-[88px] h-10 md:h-14 pointer-events-none invisible" />
                                
                                <div className="absolute left-0 top-full -translate-y-1/2 z-40">
                                    <Link 
                                        to="/" 
                                        className="navbar-floating-logo-link block group"
                                        aria-label="Ir al inicio de CR Work"
                                        onClick={() => {
                                            setIsMenuOpen(false);
                                            navigate('/');
                                            window.scrollTo({
                                                top: 0,
                                                behavior: 'smooth'
                                            });
                                        }}
                                    >
                                        <div className="navbar-floating-logo-badge w-14 h-14 md:w-20 md:h-20 lg:w-[88px] lg:h-[88px] rounded-full p-1 md:p-1.5 flex items-center justify-center bg-gradient-to-b from-neutral-900 via-black to-neutral-950 border-2 border-amber-400/60 group-hover:border-amber-400 shadow-2xl transition-all duration-300 transform group-hover:scale-105">
                                            <img
                                                src={getPublicImageUrl('cr-work-logo3.png', true)}
                                                alt="CR Work Logo"
                                                className="w-full h-full object-contain rounded-full filter drop-shadow"
                                            />
                                        </div>
                                    </Link>
                                </div>
                            </div>

                            {/* Acciones Móviles */}
                            <div className="flex items-center space-x-2 md:hidden">
                                <button
                                    onClick={() => {
                                        if (onCartClick) {
                                            onCartClick();
                                        } else {
                                            setShowQuoteDropdown(true);
                                        }
                                    }}
                                    className="quote-nav-button text-xs py-1 px-2.5 flex items-center space-x-1 rounded-lg border border-amber-400/40 bg-amber-500/10 text-amber-300"
                                >
                                    <FileText className="h-4 w-4" />
                                    <span>Cotizar</span>
                                    {cartItemsCount > 0 && (
                                        <span className="cart-badge !top-[-6px] !right-[-6px]">{cartItemsCount}</span>
                                    )}
                                </button>
                                <button
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                    className="action-button p-2 text-black hover:text-gray-900"
                                    aria-label="Abrir menú"
                                >
                                    <Menu className="nav-icon h-5 w-5" />
                                </button>
                            </div>

                            {/* Menú Desktop */}
                            <div className="hidden md:flex md:items-center md:space-x-4 lg:space-x-6">
                                {/* 1. Industrias */}
                                <div 
                                    className="relative group"
                                    onMouseEnter={() => setShowIndustriesDropdown(true)}
                                    onMouseLeave={() => setShowIndustriesDropdown(false)}
                                >
                                    <button 
                                        className="nav-link flex items-center py-2 text-sm font-medium"
                                    >
                                        <span>Industrias</span>
                                        <ChevronDown className="nav-icon w-3.5 h-3.5 ml-1" />
                                    </button>
                                    
                                    {showIndustriesDropdown && (
                                        <div className="dropdown-menu w-screen">
                                            <div className="industries-grid overflow-y-auto">
                                                {industries.map((industry) => (
                                                    <button
                                                        key={industry.name}
                                                        onClick={() => handleIndustryClick(industry.name)}
                                                        className="industry-card group"
                                                    >
                                                        <div 
                                                            className="industry-image-container" 
                                                            data-attribution="Photo: Unsplash"
                                                        >
                                                            <img
                                                                src={getPublicImageUrl(`industry-selector/dropdown-${industry.name.toLowerCase()}.webp`, true)}
                                                                alt={industry.name}
                                                                className="industry-image"
                                                                onError={(e) => {
                                                                    const currentSrc = e.currentTarget.src;
                                                                    if (currentSrc.endsWith('.webp')) {
                                                                        e.currentTarget.src = currentSrc.replace('.webp', '.jpg');
                                                                    } else if (currentSrc.endsWith('.jpg')) {
                                                                        e.currentTarget.src = currentSrc.replace('.jpg', '.jpeg');
                                                                    } else if (currentSrc.endsWith('.jpeg')) {
                                                                        e.currentTarget.src = currentSrc.replace('.jpeg', '.png');
                                                                    } else {
                                                                        e.currentTarget.src = getPublicImageUrl('banners/default-banner.jpeg', true);
                                                                    }
                                                                }}
                                                            />
                                                            <div className="industry-overlay" />
                                                        </div>
                                                        <div className="industry-content">
                                                            <h3 className="industry-title">{industry.name}</h3>
                                                            <p className="industry-description">{industry.description}</p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 2. Productos */}
                                <div 
                                    className="relative group"
                                    onMouseEnter={() => setShowProductsSearch(true)}
                                    onMouseLeave={() => setShowProductsSearch(false)}
                                >
                                    <Link 
                                        to="/catalog"
                                        className="nav-link flex items-center py-2 text-sm font-medium"
                                    >
                                        <span>Productos</span>
                                        <ChevronDown className="nav-icon w-3.5 h-3.5 ml-1" />
                                    </Link>
                                    
                                    {showProductsSearch && (
                                        <div className="dropdown-menu w-screen">
                                            {/* Sección de búsqueda en la parte superior */}
                                            <div className="border-b border-amber-300/20 p-4 flex flex-col sm:flex-row items-center gap-3">
                                                <form onSubmit={handleProductSearch} className="relative flex-1 w-full">
                                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                                    <input
                                                        type="text"
                                                        placeholder="Buscar productos..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        onKeyDown={handleSearchKeyDown}
                                                        className="w-full pl-10 pr-4 py-2 bg-black/50 border border-amber-300/30 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent backdrop-blur-sm text-sm"
                                                    />
                                                </form>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSearchTerm('');
                                                        setShowProductsSearch(false);
                                                        navigate('/catalog');
                                                    }}
                                                    className="w-full sm:w-auto whitespace-nowrap px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs flex items-center justify-center space-x-1.5 transition-colors shadow-sm"
                                                >
                                                    <span>Ver catálogo completo</span>
                                                    <ArrowRight className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                            
                                            {/* Grid de categorías */}
                                            <div className="categories-grid overflow-y-auto">
                                                {catalogStructure.map((category) => {
                                                    const IconComponent = categoryIconMap[category.id] || null;
                                                    return (
                                                        <div key={category.id} className="category-section">
                                                            <div 
                                                                onClick={() => handleCategoryClick(category.id)}
                                                                className="flex items-center space-x-2 cursor-pointer mb-2 pb-2 border-b border-amber-400/20 group/title"
                                                            >
                                                                {IconComponent && (
                                                                    <div className="text-amber-400 group-hover/title:text-amber-300 transition-colors">
                                                                        <IconComponent className="w-5 h-5" />
                                                                    </div>
                                                                )}
                                                                <h3 className="category-title !mb-0 !pb-0 !border-b-0 text-sm font-bold text-amber-400 group-hover/title:text-amber-300 transition-colors">
                                                                    {category.name}
                                                                </h3>
                                                            </div>
                                                            <div className="subcategories-list space-y-0.5">
                                                                {category.children?.map((subcategory) => {
                                                                    if (subcategory.children && subcategory.children.length > 0) {
                                                                        const isExpanded = expandedSubgroups.has(subcategory.id);
                                                                        return (
                                                                            <div key={subcategory.id} className="py-0.5 border-b border-white/5 last:border-b-0">
                                                                                <div className="flex items-center justify-between rounded hover:bg-amber-400/10 transition-colors px-1 py-0.5">
                                                                                    <button
                                                                                        onClick={() => handleCategoryClick(category.id, subcategory.id)}
                                                                                        className="subcategory-item font-semibold text-amber-300/90 hover:text-amber-200 text-xs text-left flex-grow !p-0"
                                                                                    >
                                                                                        {subcategory.name}
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={(e) => toggleSubgroup(subcategory.id, e)}
                                                                                        className="p-0.5 text-gray-400 hover:text-amber-300 transition-colors ml-1"
                                                                                        title={isExpanded ? "Colapsar subcategorías" : "Expandir subcategorías"}
                                                                                    >
                                                                                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-amber-400' : ''}`} />
                                                                                    </button>
                                                                                </div>
                                                                                {isExpanded && (
                                                                                    <div className="pl-2 space-y-0.5 mt-1 pb-1 border-l border-amber-500/40 ml-1.5">
                                                                                        {subcategory.children.map((child) => (
                                                                                            <button
                                                                                                key={child.id}
                                                                                                onClick={() => handleCategoryClick(subcategory.id, child.id)}
                                                                                                className="subcategory-item text-[11px] text-gray-300 hover:text-amber-300 text-left w-full block py-0.5 transition-colors pl-1"
                                                                                            >
                                                                                                • {child.name}
                                                                                            </button>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <button
                                                                            key={subcategory.id}
                                                                            onClick={() => handleCategoryClick(category.id, subcategory.id)}
                                                                            className="subcategory-item text-left w-full text-xs text-gray-300 hover:text-amber-300 block py-0.5 transition-colors"
                                                                        >
                                                                            {subcategory.name}
                                                                        </button>
                                                                    );
                                                                }) || (
                                                                    <button
                                                                        onClick={() => handleCategoryClick(category.id)}
                                                                        className="subcategory-item text-left w-full text-xs text-gray-300 hover:text-amber-300"
                                                                    >
                                                                        Ver todos
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 3. Marcas */}
                                <div 
                                    className="relative group"
                                    onMouseEnter={() => setShowBrandsDropdown(true)}
                                    onMouseLeave={() => setShowBrandsDropdown(false)}
                                >
                                    <Link 
                                        to="/catalog"
                                        className="nav-link flex items-center py-2 text-sm font-medium"
                                    >
                                        <span>Marcas</span>
                                        <ChevronDown className="nav-icon w-3.5 h-3.5 ml-1" />
                                    </Link>
                                    
                                    {showBrandsDropdown && (
                                        <div className="dropdown-menu w-screen max-w-5xl left-1/2 -translate-x-1/2">
                                            <div className="p-4 border-b border-amber-300/20 flex justify-between items-center bg-amber-500/5">
                                                <div>
                                                    <h3 className="text-sm font-bold text-amber-300 uppercase tracking-wider">Marcas Líderes</h3>
                                                    <p className="text-xs text-gray-400">Distribuimos las mejores marcas en seguridad industrial y EPP</p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setShowBrandsDropdown(false);
                                                        navigate('/catalog');
                                                    }}
                                                    className="text-xs text-amber-300 hover:text-amber-200 underline font-medium"
                                                >
                                                    Ver todas en Catálogo →
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 p-4 max-h-[60vh] overflow-y-auto">
                                                {PARTNER_BRANDS.map((brand) => (
                                                    <button
                                                        key={brand.name}
                                                        onClick={() => handleBrandClick(brand.name)}
                                                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-white/5 hover:bg-amber-400/10 border border-white/10 hover:border-amber-400/40 transition-all group/brand h-20"
                                                    >
                                                        <div className="h-9 w-full flex items-center justify-center">
                                                            <BrandImage
                                                                src={brand.logo}
                                                                alt={brand.name}
                                                                className="max-h-8 max-w-[80%] object-contain"
                                                                fallbackType="text"
                                                            />
                                                        </div>
                                                        <span className="text-[11px] font-semibold text-gray-300 group-hover/brand:text-amber-300 mt-1 transition-colors">
                                                            {brand.name}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 4. Empresa (antes Nosotros) */}
                                <Link to="/nosotros" className="nav-link text-sm font-medium py-2">
                                    Empresa
                                </Link>

                                {/* 5. Contacto */}
                                <div 
                                    className="relative group"
                                    onMouseEnter={() => setShowContactDropdown(true)}
                                    onMouseLeave={() => setShowContactDropdown(false)}
                                >
                                    <button 
                                        onClick={handleContactClick}
                                        className="nav-link flex items-center py-2 text-sm font-medium cursor-pointer"
                                    >
                                        <span>Contacto</span>
                                        <ChevronDown className="nav-icon w-3.5 h-3.5 ml-1" />
                                    </button>
                                    
                                    {showContactDropdown && (
                                        <div className="dropdown-menu !w-80 p-4 left-1/2 -translate-x-1/2">
                                            <div className="pb-3 border-b border-amber-300/20 mb-3">
                                                <h3 className="text-sm font-bold text-amber-300">Atención Comercial</h3>
                                                <p className="text-xs text-gray-400">Comunícate con nuestros asesores de seguridad</p>
                                            </div>
                                            <div className="space-y-3">
                                                <button 
                                                    onClick={() => {
                                                        setShowContactDropdown(false);
                                                        setIsMapOpen(true);
                                                    }}
                                                    className="flex items-start space-x-2.5 text-left w-full hover:text-amber-400 text-gray-300 transition-colors group/c"
                                                >
                                                    <MapPin className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0 group-hover/c:scale-110 transition-transform" />
                                                    <div className="text-xs">
                                                        <p className="font-semibold text-white group-hover/c:text-amber-300">Dirección</p>
                                                        <p className="text-gray-400">Merlo 2338, Moreno, Buenos Aires</p>
                                                    </div>
                                                </button>

                                                <a 
                                                    href="tel:02374636894"
                                                    className="flex items-start space-x-2.5 text-left w-full hover:text-amber-400 text-gray-300 transition-colors group/c"
                                                >
                                                    <Phone className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0 group-hover/c:scale-110 transition-transform" />
                                                    <div className="text-xs">
                                                        <p className="font-semibold text-white group-hover/c:text-amber-300">Teléfono</p>
                                                        <p className="text-gray-400">0237-4636894</p>
                                                    </div>
                                                </a>

                                                <a 
                                                    href="mailto:ignacio@crseguridad.com"
                                                    className="flex items-start space-x-2.5 text-left w-full hover:text-amber-400 text-gray-300 transition-colors group/c"
                                                >
                                                    <Mail className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0 group-hover/c:scale-110 transition-transform" />
                                                    <div className="text-xs">
                                                        <p className="font-semibold text-white group-hover/c:text-amber-300">Email</p>
                                                        <p className="text-gray-400">ignacio@crseguridad.com</p>
                                                    </div>
                                                </a>

                                                <div className="flex items-start space-x-2.5 text-left w-full text-gray-300">
                                                    <Clock className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                                    <div className="text-xs">
                                                        <p className="font-semibold text-white">Horario</p>
                                                        <p className="text-gray-400">Lun a Vie: 8:30 a 17:30hs</p>
                                                    </div>
                                                </div>

                                                <div className="pt-3 border-t border-amber-300/20 flex items-center justify-between">
                                                    <span className="text-[11px] text-gray-400">Canales directos:</span>
                                                    <div className="flex items-center space-x-3">
                                                        <a 
                                                            href="https://wa.me/541152218882?text=Hola%2C%20vi%20el%20nuevo%20sitio%20web%20y%20me%20interesan%20algunos%20productos." 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-gray-400 hover:text-green-400 transition-colors"
                                                            title="WhatsApp"
                                                        >
                                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                            </svg>
                                                        </a>
                                                        <a 
                                                            href="https://www.instagram.com/crwork.ok/" 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-gray-400 hover:text-amber-400 transition-colors"
                                                            title="Instagram"
                                                        >
                                                            <Instagram className="w-4 h-4" />
                                                        </a>
                                                        <a 
                                                            href="https://www.linkedin.com/company/cr-work-seguridad-industrial/" 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-gray-400 hover:text-blue-400 transition-colors"
                                                            title="LinkedIn"
                                                        >
                                                            <Linkedin className="w-4 h-4" />
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 6. Buscador */}
                                <div 
                                    className="relative group"
                                    ref={searchDropdownRef}
                                    onMouseEnter={() => setShowSearchDropdown(true)}
                                    onMouseLeave={() => setShowSearchDropdown(false)}
                                >
                                    <button 
                                        onClick={() => setShowSearchDropdown(prev => !prev)}
                                        className="nav-link flex items-center py-2 text-sm font-medium space-x-1"
                                    >
                                        <Search className="w-3.5 h-3.5 text-amber-300" />
                                        <span>Buscador</span>
                                        <ChevronDown className="nav-icon w-3.5 h-3.5 ml-0.5" />
                                    </button>

                                    {showSearchDropdown && (
                                        <div className="dropdown-menu !w-80 md:!w-96 p-4 left-1/2 -translate-x-1/2">
                                            <form onSubmit={handleSearchSubmit} className="relative">
                                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                                <input
                                                    ref={navSearchInputRef}
                                                    type="text"
                                                    placeholder="Buscar productos..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    onKeyDown={handleSearchKeyDown}
                                                    autoFocus
                                                    className="w-full pl-10 pr-4 py-2 bg-black/70 border border-amber-300/40 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-400 backdrop-blur-sm text-sm"
                                                />
                                            </form>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSearchTerm('');
                                                    setShowSearchDropdown(false);
                                                    navigate('/catalog');
                                                }}
                                                className="w-full mt-2.5 py-2 px-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs flex items-center justify-center space-x-1.5 transition-all shadow-sm hover:shadow-amber-500/20"
                                            >
                                                <span>Ver catálogo completo</span>
                                                <ArrowRight className="w-3.5 h-3.5" />
                                            </button>

                                            <div className="mt-3 flex flex-wrap gap-1.5 pt-2.5 border-t border-amber-300/10">
                                                <span className="text-[11px] text-gray-400 w-full mb-1">Búsquedas rápidas:</span>
                                                {['Cascos', 'Guantes', 'Calzado', 'Protección Ocular', '3M', 'Libus', 'Ombú'].map((tag) => (
                                                    <button
                                                        key={tag}
                                                        onClick={() => {
                                                            setSearchTerm('');
                                                            setShowSearchDropdown(false);
                                                            navigate(`/catalog?search=${encodeURIComponent(tag)}`);
                                                        }}
                                                        className="text-[11px] px-2 py-0.5 rounded bg-amber-400/10 hover:bg-amber-400/20 text-amber-300 border border-amber-400/20 transition-colors"
                                                    >
                                                        {tag}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 7. Mi Cotización (Botón Textual y Dropdown) */}
                                <div 
                                    className="relative"
                                    ref={quoteDropdownRef}
                                >
                                    <button
                                        onClick={() => setShowQuoteDropdown(prev => !prev)}
                                        className="quote-nav-button relative flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border border-amber-400/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 transition-all shadow-sm"
                                    >
                                        <FileText className="w-4 h-4 text-amber-400" />
                                        <span className="font-semibold text-sm">Mi Cotización</span>
                                        {cartItemsCount > 0 && (
                                            <span className="cart-badge">{cartItemsCount}</span>
                                        )}
                                    </button>

                                    {showQuoteDropdown && (
                                        <div className="dropdown-menu !w-88 md:!w-96 right-0 left-auto translate-x-0 !opacity-100 !pointer-events-auto !scale-100 mt-2">
                                            <div className="p-3.5 border-b border-amber-300/20 flex items-center justify-between bg-amber-500/10">
                                                <div className="flex items-center space-x-2">
                                                    <FileText className="w-4 h-4 text-amber-400" />
                                                    <h3 className="text-sm font-bold text-amber-300">Elementos a Cotizar</h3>
                                                </div>
                                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300 font-semibold border border-amber-400/30">
                                                    {cartItemsCount} {cartItemsCount === 1 ? 'ítem' : 'ítems'}
                                                </span>
                                            </div>

                                            {cartItems.length === 0 ? (
                                                <div className="p-6 text-center space-y-3">
                                                    <div className="w-12 h-12 rounded-full bg-amber-400/10 flex items-center justify-center mx-auto text-amber-400 border border-amber-400/20">
                                                        <FileText className="w-6 h-6" />
                                                    </div>
                                                    <p className="text-xs text-gray-300">No tienes productos en tu lista de cotización.</p>
                                                    <button
                                                        onClick={() => {
                                                            setShowQuoteDropdown(false);
                                                            navigate('/catalog');
                                                        }}
                                                        className="text-xs px-4 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold transition-colors"
                                                    >
                                                        Explorar Catálogo
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="max-h-72 overflow-y-auto p-3 space-y-2.5 divide-y divide-white/5">
                                                        {cartItems.map((item) => (
                                                            <div key={item._id} className="pt-2 first:pt-0 flex items-center space-x-3 group/item">
                                                                <div className="w-10 h-10 bg-white/10 rounded-lg p-1 flex-shrink-0 border border-amber-300/20 overflow-hidden flex items-center justify-center">
                                                                    <img
                                                                        src={item.image_url || '/images/placeholder-product.jpg'}
                                                                        alt={item.name}
                                                                        className="w-full h-full object-contain"
                                                                        onError={(e) => {
                                                                            (e.target as HTMLImageElement).src = '/images/placeholder-product.jpg';
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-xs font-semibold text-white truncate group-hover/item:text-amber-300 transition-colors">
                                                                        {item.name}
                                                                    </p>
                                                                    <div className="flex items-center space-x-1.5 text-[10px] text-gray-400">
                                                                        {item.brand && <span className="text-amber-400/90 font-medium">{item.brand}</span>}
                                                                        {item.brand && <span>•</span>}
                                                                        <span className="truncate">{item.category}</span>
                                                                    </div>
                                                                    {FEATURE_FLAGS.SHOW_CART_PRICING && isAuthenticated && (
                                                                        <p className="text-xs text-amber-400 font-bold mt-0.5">
                                                                            ${Number(item.price).toFixed(2)}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center space-x-1 flex-shrink-0">
                                                                    <button
                                                                        onClick={() => onUpdateQuantity?.(item._id, Math.max(0, item.quantity - 1))}
                                                                        className="w-5 h-5 flex items-center justify-center rounded bg-white/10 hover:bg-amber-400/20 text-gray-300 hover:text-amber-300 text-xs border border-white/10"
                                                                        title="Disminuir"
                                                                    >
                                                                        -
                                                                    </button>
                                                                    <span className="w-4 text-center text-xs font-semibold text-amber-300">{item.quantity}</span>
                                                                    <button
                                                                        onClick={() => onUpdateQuantity?.(item._id, item.quantity + 1)}
                                                                        className="w-5 h-5 flex items-center justify-center rounded bg-white/10 hover:bg-amber-400/20 text-gray-300 hover:text-amber-300 text-xs border border-white/10"
                                                                        title="Aumentar"
                                                                    >
                                                                        +
                                                                    </button>
                                                                    <button
                                                                        onClick={() => onRemoveItem?.(item._id)}
                                                                        className="w-5 h-5 flex items-center justify-center text-red-400 hover:text-red-300 ml-0.5"
                                                                        title="Eliminar"
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="p-3 border-t border-amber-300/20 bg-black/60 space-y-2">
                                                        {FEATURE_FLAGS.SHOW_CART_PRICING && isAuthenticated && (
                                                            <div className="flex justify-between items-center text-xs text-gray-300 font-medium pb-1">
                                                                <span>Total Estimado:</span>
                                                                <span className="text-sm font-bold text-amber-400">
                                                                    ${totalQuotePrice.toFixed(2)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    setShowQuoteDropdown(false);
                                                                    navigate('/order-detail');
                                                                }}
                                                                disabled={cartItems.length === 0}
                                                                className="flex-1 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-amber-300 text-xs font-semibold border border-amber-300/30 transition-all text-center disabled:opacity-50"
                                                            >
                                                                Ver Detalle
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    if (cartItems.length === 0) return;
                                                                    if (isAuthenticated) {
                                                                        handleQuoteRequest({
                                                                            fullName: userProfile?.name || user?.user_metadata?.name || 'Usuario Autenticado',
                                                                            company: userProfile?.company || 'Empresa Registrada',
                                                                            email: user?.email || 'usuario@email.com'
                                                                        });
                                                                    } else if (FEATURE_FLAGS.ALLOW_GUEST_QUOTE_REQUEST) {
                                                                        setShowQuoteModal(true);
                                                                    } else {
                                                                        navigate('/login');
                                                                    }
                                                                }}
                                                                disabled={cartItems.length === 0}
                                                                className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black text-xs font-bold shadow-md shadow-amber-500/20 transition-all text-center disabled:opacity-50"
                                                            >
                                                                Solicitar Cotización
                                                            </button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Botones de acción - Derecha */}
                            <div className="hidden md:flex items-center space-x-3">
                                {renderAuthButtons()}
                            </div>
                        </div>
                    </div>
                </nav>
            </header>
            <div className="navbar-spacer" />

            {/* Menú móvil desplegable fullscreen (fuera del header para evitar restricciones de altura/backdrop-filter) */}
            {isMenuOpen && (
                <div 
                    className="fixed md:hidden inset-0 bg-black/95 backdrop-blur-2xl z-[99999] overflow-y-auto"
                >
                    <div 
                        ref={menuRef}
                        className="min-h-full flex flex-col justify-between p-6 max-w-md mx-auto"
                    >
                        {/* Botón de cerrar y logo */}
                        <div className="flex justify-between items-center pb-4 border-b border-amber-300/20">
                            <div className="flex items-center space-x-2">
                                <img
                                    src={getPublicImageUrl('cr-work-logo3.png', true)}
                                    alt="CR Work"
                                    className="h-8 w-8 object-contain"
                                />
                                <span className="font-bold text-amber-400 text-base">CR Work</span>
                            </div>
                            <button
                                onClick={() => setIsMenuOpen(false)}
                                className="text-amber-300 hover:text-amber-400 p-1"
                                aria-label="Cerrar menú"
                            >
                                <X className="h-7 w-7" />
                            </button>
                        </div>
                        
                        {/* Contenido del menú */}
                        <div className="flex flex-col space-y-5 py-6">
                            {/* Buscador móvil */}
                            <div className="space-y-2 w-full">
                                <form onSubmit={handleSearchSubmit} className="relative w-full">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Buscar en el catálogo..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 bg-white/10 border border-amber-300/30 rounded-xl text-white placeholder-gray-400 text-sm focus:outline-none focus:border-amber-400"
                                    />
                                </form>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setIsMenuOpen(false);
                                        navigate('/catalog');
                                    }}
                                    className="w-full py-2 px-3 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/30 text-amber-300 font-semibold text-xs flex items-center justify-center space-x-1.5 transition-colors"
                                >
                                    <span>Ver catálogo completo</span>
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            {/* Industrias */}
                            <div className="border-b border-white/10 pb-3">
                                <button 
                                    onClick={() => setShowIndustriesMenu(!showIndustriesMenu)}
                                    className="flex items-center justify-between text-lg text-amber-300 hover:text-amber-400 w-full font-medium"
                                >
                                    <span>Industrias</span>
                                    <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${showIndustriesMenu ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {showIndustriesMenu && (
                                    <div className="grid grid-cols-2 gap-2 mt-3 pl-2">
                                        {industries.map((industry) => (
                                            <button
                                                key={industry.name}
                                                onClick={() => handleIndustryClick(industry.name)}
                                                className="text-left text-xs text-gray-300 hover:text-amber-300 py-1.5 px-2 rounded bg-white/5 truncate"
                                            >
                                                {industry.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Productos */}
                            <Link 
                                to="/catalog" 
                                className="text-lg text-amber-300 hover:text-amber-400 font-medium border-b border-white/10 pb-3 block"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Productos
                            </Link>
                            
                            {/* Marcas */}
                            <div className="border-b border-white/10 pb-3">
                                <button 
                                    onClick={() => setShowBrandsMenu(!showBrandsMenu)}
                                    className="flex items-center justify-between text-lg text-amber-300 hover:text-amber-400 w-full font-medium"
                                >
                                    <span>Marcas</span>
                                    <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${showBrandsMenu ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {showBrandsMenu && (
                                    <div className="grid grid-cols-3 gap-2 mt-3 pl-2">
                                        {PARTNER_BRANDS.map((brand) => (
                                            <button
                                                key={brand.name}
                                                onClick={() => handleBrandClick(brand.name)}
                                                className="text-center text-xs font-semibold text-gray-300 hover:text-amber-300 py-2 px-1 rounded bg-white/5 border border-white/10 truncate"
                                            >
                                                {brand.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Empresa */}
                            <Link 
                                to="/nosotros" 
                                className="text-lg text-amber-300 hover:text-amber-400 font-medium border-b border-white/10 pb-3 block"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Empresa
                            </Link>

                            {/* Contacto */}
                            <button 
                                onClick={handleContactClick}
                                className="text-left text-lg text-amber-300 hover:text-amber-400 font-medium border-b border-white/10 pb-3 block w-full"
                            >
                                Contacto
                            </button>

                            {/* Mi Cotización */}
                            <button 
                                onClick={() => {
                                    setIsMenuOpen(false);
                                    navigate('/order-detail');
                                }}
                                className="flex items-center justify-between text-lg text-amber-400 font-bold border-b border-white/10 pb-3 w-full text-left"
                            >
                                <span className="flex items-center space-x-2">
                                    <FileText className="w-5 h-5" />
                                    <span>Mi Cotización</span>
                                </span>
                                <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30">
                                    {cartItemsCount}
                                </span>
                            </button>
                            
                            {/* Autenticación */}
                            <div className="space-y-3 pt-2">
                                {renderMobileAuthButtons()}
                            </div>
                        </div>

                        {/* Footer del Drawer */}
                        <div className="pt-4 border-t border-amber-300/20 text-center">
                            <p className="text-xs text-gray-400">CR Work Seguridad Industrial</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Mapa */}
            <MapPopup isOpen={isMapOpen} onClose={() => setIsMapOpen(false)} />

            {/* Modal de Cotización para usuarios no autenticados */}
            {FEATURE_FLAGS.ALLOW_GUEST_QUOTE_REQUEST && (
                <QuoteRequestModal
                    isOpen={showQuoteModal}
                    onClose={() => setShowQuoteModal(false)}
                    onSubmit={handleQuoteRequest}
                />
            )}

            {/* Modal de Autenticación */}
            {showLogin && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
                        <button
                            onClick={handleCloseLogin}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {isRegistering ? 'Crear cuenta' : 'Iniciar Sesión'}
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                {isRegistering 
                                    ? 'Completa tus datos para crear una cuenta'
                                    : 'Ingresa tus credenciales para continuar'
                                }
                            </p>
                        </div>

                        {isRegistering ? (
                            <UserRegistration 
                                onSwitchToLogin={handleSwitchToLogin}
                                setShowLogin={setShowLogin}
                            />
                        ) : (
                            <LoginForm 
                                setShowLogin={setShowLogin}
                                onSwitchToRegister={handleSwitchToRegister}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Navbar;
